import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import { usePartnerStatuses } from '../../constants/partnerStatuses';
import StatusBadge from '../../components/doorlypro/StatusBadge';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Partner-facing status palette + copy now lives in one place:
// frontend/src/constants/partnerStatuses.js (mirrors backend/utils/partnerStatus.js).

// 🔑 StatusBadge MOVED TO components/doorlypro/StatusBadge.jsx so My Borrowers
// renders the identical pill. Two pages each deciding how one vocabulary looks
// is how they come to disagree about the same stage.

function StatusTooltip() {
  // Hook, not the static import: the list must re-render once backend copy
  // lands from /api/knowledge, and must still render the bootstrap copy before
  // it does (or if the endpoint is down).
  const partnerStatuses = usePartnerStatuses();
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = React.useRef(null);

  const calcPos = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const pw = 320;
    let left = r.left;
    if (left + pw > window.innerWidth - 16) left = window.innerWidth - pw - 16;
    if (left < 16) left = 16;
    setPos({ top: r.bottom + 8, left });
  };

  const open = () => { calcPos(); setVisible(true); };
  const close = () => setVisible(false);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => (visible ? close() : open())}
        style={{
          width: 18, height: 18, borderRadius: '50%',
          background: visible ? '#3a6ad9' : '#e5e7eb',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', flexShrink: 0, border: 'none', padding: 0,
          transition: 'background 0.15s', verticalAlign: 'middle',
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={visible ? '#fff' : '#6b7280'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </button>
      {visible && createPortal(
        <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />,
        document.body
      )}
      {visible && createPortal(
        <div style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          width: 320,
          maxHeight: '70vh',
          overflowY: 'auto',
          zIndex: 9999,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
          padding: 16,
          boxSizing: 'border-box',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em' }}>What do these statuses mean?</span>
            <button type="button" onClick={close} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>&times;</button>
          </div>
          {partnerStatuses.map(s => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '3px 6px', borderRadius: 6,
                background: s.color.bg, color: s.color.color, border: `1px solid ${s.color.color}30`,
                whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1.4, marginTop: 1,
                minWidth: 96, textAlign: 'center',
              }}>{s.label}</span>
              <span style={{
                fontSize: 11, lineHeight: 1.5,
                flex: 1, minWidth: 0,
                wordBreak: 'break-word', whiteSpace: 'normal',
              }}>
                <span style={{ color: '#374151', fontWeight: 600 }}>{s.definition}</span>
                <br />
                <span style={{ color: '#9ca3af' }}>{s.nextStep}</span>
              </span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

export default function DoorlyProReferrals() {
  const { token } = useDoorlyProAuth();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resendingId, setResendingId] = useState(null);
  const [resendMsg, setResendMsg] = useState({});

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/doorlypro/referrals`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setReferrals(res.data?.referrals || res.data || []))
      .catch(() => setError('Failed to load buyers.'))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>My Buyers</h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 14px' }}>Track and manage all of your submitted buyers.</p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <Link
              to="/doorlypro/referrals/new"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 20px', borderRadius: 10, backgroundColor: '#3a6ad9',
                color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none',
              }}
            >
              + Submit a Buyer
            </Link>
            <Link
              to="/doorlypro/borrower-journey"
              style={{ fontSize: 13, fontWeight: 600, color: '#3a6ad9', textDecoration: 'none' }}
            >
              View Borrower Journey &rarr;
            </Link>
          </div>
        </div>

        <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {error && (
            <div style={{ padding: '12px 20px', backgroundColor: '#fff0f3', color: '#ff082f', fontSize: 13 }}>{error}</div>
          )}

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading buyers...</div>
          ) : referrals.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 12 }}>You haven't submitted any buyers yet.</p>
              <Link
                to="/doorlypro/referrals/new"
                style={{ color: '#3a6ad9', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
              >
                Submit your first buyer &rarr;
              </Link>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    {['Buyer ID', 'Buyer Name', 'Email', 'Status', 'Created', 'Action'].map((col) => (
                      <th key={col} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap', fontSize: 12 }}>
                        {col === 'Status' ? (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            Status <StatusTooltip />
                          </div>
                        ) : col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r, idx) => (
                    <tr key={r.id} style={{ borderBottom: idx < referrals.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <td style={{ padding: '12px 16px', color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>
                        #{String(r.id).padStart(4, '0')}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                        {r.first_name} {r.last_name}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#374151' }}>{r.email}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <StatusBadge status={r.partner_status} />
                      </td>
                      <td style={{ padding: '12px 16px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Link to={`/doorlypro/referrals/${r.id}`} style={{ color: '#3a6ad9', fontWeight: 600, fontSize: 12, textDecoration: 'none' }}>View</Link>
                          {['pre_qualified', 'invited', 'pending'].includes(r.display_status || r.status) && (
                            resendMsg[r.id] ? (
                              <span style={{ fontSize: 11, color: resendMsg[r.id].startsWith('✓') ? '#166534' : '#ff082f', fontWeight: 600 }}>{resendMsg[r.id]}</span>
                            ) : (
                              <button onClick={async (e) => {
                                e.stopPropagation();
                                setResendingId(r.id);
                                try {
                                  await axios.post(`${API_URL}/api/doorlypro/referrals/${r.id}/resend-invite`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                  setResendMsg(p => ({ ...p, [r.id]: '✓ Sent' }));
                                  setTimeout(() => setResendMsg(p => ({ ...p, [r.id]: null })), 4000);
                                } catch (err) {
                                  setResendMsg(p => ({ ...p, [r.id]: err.response?.data?.error || 'Failed' }));
                                  setTimeout(() => setResendMsg(p => ({ ...p, [r.id]: null })), 4000);
                                }
                                setResendingId(null);
                              }} disabled={resendingId === r.id}
                                style={{ padding: '3px 10px', background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.63"/></svg>
                                {resendingId === r.id ? '...' : 'Resend'}
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DoorlyProLayout>
    </RequirePartnerAuth>
  );
}
